import { DAY_KEYS } from '../../../lib/outreach/types'
import type {
  DayKey,
  Program,
  ProgramInput,
  ProgramKind,
  RampRow,
} from '../../../lib/outreach/types'
import { parsePhraseList } from '../../../lib/outreach/merge'
import { sortRamp, validateRamp } from '../../../lib/outreach/ramp'
import { parseWhole } from './shared'

/** Form state for a program. Numbers stay as text until validation. */
export interface RampDraftRow {
  from: string
  cap: string
}

export interface ProgramDraft {
  name: string
  kind: ProgramKind
  siteId: string
  fromLabel: string
  dailyCap: string
  ramp: RampDraftRow[]
  cooldown: string
  neverSame: boolean
  bouncePct: string
  bounceMin: string
  days: Record<DayKey, boolean>
  phrases: string
  sequenceId: string
  active: boolean
}

export type DraftErrorKey = 'name' | 'dailyCap' | 'ramp' | 'cooldown' | 'bounce' | 'bounceMin'

export interface DraftResult {
  errors: Partial<Record<DraftErrorKey, string>>
  warnings: string[]
  /** Null while there are errors. */
  input: ProgramInput | null
}

const DEFAULT_DAYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri']

function daysFrom(list: readonly DayKey[]): Record<DayKey, boolean> {
  return Object.fromEntries(DAY_KEYS.map((d) => [d, list.includes(d)])) as Record<DayKey, boolean>
}

export function draftFromProgram(p: Program | null): ProgramDraft {
  if (!p) {
    return {
      name: '',
      kind: 'sales',
      siteId: '',
      fromLabel: '',
      dailyCap: '20',
      ramp: [],
      cooldown: '30',
      neverSame: true,
      bouncePct: '2',
      bounceMin: '20',
      days: daysFrom(DEFAULT_DAYS),
      phrases: '',
      sequenceId: '',
      active: false,
    }
  }
  return {
    name: p.name,
    kind: p.kind,
    siteId: p.site_id ?? '',
    fromLabel: p.from_label ?? '',
    dailyCap: String(p.daily_cap),
    ramp: p.ramp.map((r) => ({ from: r.from, cap: String(r.cap) })),
    cooldown: String(p.cooldown_days),
    neverSame: p.never_same_address,
    bouncePct: String(p.bounce_stop_pct),
    bounceMin: String(p.bounce_min_sample),
    days: daysFrom(p.send_days),
    phrases: p.blocked_phrases.join('\n'),
    sequenceId: p.sequence_id ?? '',
    active: p.active,
  }
}

/** Ramp rows as numbers. A blank or non-whole cap becomes NaN so validateRamp rejects it. */
export function rampFromDraft(rows: readonly RampDraftRow[]): RampRow[] {
  return rows
    .filter((r) => r.from.trim() !== '' || r.cap.trim() !== '')
    .map((r) => ({ from: r.from.trim(), cap: parseWhole(r.cap) }))
}

export function validateDraft(d: ProgramDraft): DraftResult {
  const errors: DraftResult['errors'] = {}
  const warnings: string[] = []

  const name = d.name.trim()
  if (!name) errors.name = 'Give the program a name.'
  else if (name.length > 120) errors.name = 'The name is too long (120 characters at most).'

  const dailyCap = parseWhole(d.dailyCap)
  if (Number.isNaN(dailyCap)) errors.dailyCap = 'Daily cap must be a whole number, 0 or more.'

  const cooldown = parseWhole(d.cooldown)
  if (Number.isNaN(cooldown))
    errors.cooldown = 'Cooldown must be a whole number of days, 0 or more.'

  const bounce = d.bouncePct.trim() === '' ? Number.NaN : Number(d.bouncePct)
  if (!Number.isFinite(bounce) || bounce < 0 || bounce > 100) {
    errors.bounce = 'Bounce stop must be a number from 0 to 100.'
  }

  const bounceMin = parseWhole(d.bounceMin)
  if (Number.isNaN(bounceMin)) {
    errors.bounceMin = 'Minimum sends must be a whole number, 0 or more.'
  }

  const ramp = rampFromDraft(d.ramp)
  const rampError = validateRamp(ramp)
  if (rampError) errors.ramp = rampError

  const sendDays = DAY_KEYS.filter((k) => d.days[k])
  if (sendDays.length === 0)
    warnings.push('No send days are ticked, so this program will never send.')

  if (Object.keys(errors).length > 0) return { errors, warnings, input: null }

  return {
    errors,
    warnings,
    input: {
      name,
      kind: d.kind,
      site_id: d.siteId || null,
      from_label: d.fromLabel.trim() || null,
      daily_cap: dailyCap,
      ramp: sortRamp(ramp),
      cooldown_days: cooldown,
      never_same_address: d.neverSame,
      bounce_stop_pct: bounce,
      bounce_min_sample: bounceMin,
      send_days: sendDays,
      blocked_phrases: parsePhraseList(d.phrases),
      active: d.active,
      sequence_id: d.sequenceId || null,
    },
  }
}
